import { createNavigation } from "next-intl/navigation";
import { defineRouting } from "next-intl/routing";
import campaignsPagePath from "../app/[locale]/(editorial)/campaigns/[[...slug]]/_routes.json";
import explorePagePath from "../app/[locale]/(editorial)/explore/[[...slug]]/_routes.json";
import guidesPagePath from "../app/[locale]/(editorial)/guides/[[...slug]]/_routes.json";
import storiesPagePath from "../app/[locale]/(editorial)/stories/[[...slug]]/_routes.json";
import orderPagePath from "../app/[locale]/(order)/order/_routes.json";
import orderConfirmationPagePath from "../app/[locale]/(order)/order/confirmation/_routes.json";
import checkoutPagePath from "../app/[locale]/checkout/_routes.json";
import topLevelPagePath from "../app/_routes.json";
import productPagePath from "../app/[locale]/product/_routes.json";
import orderTrackingPagePath from "../app/[locale]/(order)/order-tracking/_routes.json";
import myAccountPagePath from "../app/[locale]/(account)/my-account/_routes.json";
import customerServicePagePath from "../app/[locale]/customer-service/[...slug]/_routes.json";
import customerServiceLegalPages from "../app/[locale]/customer-service/legal-pages/[...slug]/_routes.json";
import bagsAndAccesoriesPagePath from "../app/[locale]/(listing)/bags-and-accessories/_routes.json";
import blackFridayPagePath from "../app/[locale]/(listing)/black-friday/_routes.json";
import collectionsPagePath from "../app/[locale]/(listing)/collections/_routes.json";
import cyberMondayPagePath from "../app/[locale]/(listing)/cyber-monday/_routes.json";
import golfPagePath from "../app/[locale]/(listing)/golf/_routes.json";
import kidsPagePath from "../app/[locale]/(listing)/kids/_routes.json";
import menPagePath from "../app/[locale]/(listing)/men/_routes.json";
import newPagePath from "../app/[locale]/(listing)/new/_routes.json";
import outdoorPagePath from "../app/[locale]/(listing)/outdoor/_routes.json";
import salePagePath from "../app/[locale]/(listing)/sale/_routes.json";
import shoeCarePagePath from "../app/[locale]/(listing)/shoe-care/_routes.json";
import womenPagePath from "../app/[locale]/(listing)/women/_routes.json";
import addressesPagePath from "../app/[locale]/(account)/my-account/addresses/_routes.json";
import eccoClubPagePath from "../app/[locale]/(account)/my-account/ecco-club/_routes.json";
import ordersPagePath from "../app/[locale]/(account)/my-account/orders/_routes.json";
import profilePagePath from "../app/[locale]/(account)/my-account/profile/_routes.json";
import subscriptionsPagePath from "../app/[locale]/(account)/my-account/subscriptions/_routes.json";
import wishlistPagePath from "../app/[locale]/(account)/my-account/wishlist/_routes.json";

const rawLocales = process.env.NEXT_PUBLIC_LOCALES?.split(",") ?? [];
if (rawLocales.length === 0) {
    throw new Error("NEXT_PUBLIC_LOCALES environment variable is not set or empty.");
}
const locales = rawLocales;
const defaultLocale = locales[0];

const systemRoutes = [
    checkoutPagePath["/"],
    orderPagePath["/"],
    topLevelPagePath["shopping-bag"],
    topLevelPagePath["store-locator"],
    orderTrackingPagePath["/"],
    productPagePath["/"],
    myAccountPagePath["/"],
    topLevelPagePath["search"],
    topLevelPagePath["log-in"],
    topLevelPagePath["create-account"],
    topLevelPagePath["reset-password"],
    customerServicePagePath["/"],
    bagsAndAccesoriesPagePath["/"],
    blackFridayPagePath["/"],
    collectionsPagePath["/"],
    cyberMondayPagePath["/"],
    golfPagePath["/"],
    kidsPagePath["/"],
    menPagePath["/"],
    newPagePath["/"],
    outdoorPagePath["/"],
    salePagePath["/"],
    shoeCarePagePath["/"],
    womenPagePath["/"],
];

const editorialRoutes = [
    { key: "stories", path: storiesPagePath },
    { key: "guides", path: guidesPagePath },
    { key: "explore", path: explorePagePath },
    { key: "campaigns", path: campaignsPagePath },
    { key: "customer-service", path: customerServicePagePath },
];

const myAccountPagesList = [
    { key: "addresses", path: addressesPagePath },
    { key: "ecco-club", path: eccoClubPagePath },
    { key: "orders", path: ordersPagePath },
    { key: "profile", path: profilePagePath },
    { key: "subscriptions", path: subscriptionsPagePath },
    { key: "wishlist", path: wishlistPagePath },
];

const categoryRoutes = [
    { key: "bags-and-accessories", path: bagsAndAccesoriesPagePath },
    { key: "black-friday", path: blackFridayPagePath },
    { key: "collections", path: collectionsPagePath },
    { key: "cyber-monday", path: cyberMondayPagePath },
    { key: "golf", path: golfPagePath },
    { key: "kids", path: kidsPagePath },
    { key: "men", path: menPagePath },
    { key: "new", path: newPagePath },
    { key: "outdoor", path: outdoorPagePath },
    { key: "sale", path: salePagePath },
    { key: "shoe-care", path: shoeCarePagePath },
    { key: "women", path: womenPagePath },
];

type LocalePaths = {
    [locale: string]: string;
};

type Pathnames = {
    [path: string]: LocalePaths;
};

const generatePathnames = (locales: string[]): Pathnames => {
    const pathnames: Pathnames = {};
    const addRoute = (locale: string, key: string, routePath: string) => {
        if (!pathnames[key]) {
            pathnames[key] = {};
        }
        // Ensure decoding or routes with special characters will not work.
        // Dynamic route params and slashes are safe from decodeURIComponent.
        pathnames[decodeURIComponent(key)][locale] = decodeURIComponent(routePath);
    };

    for (const locale of locales) {
        for (const route of systemRoutes) {
            if (route[locale] != null) {
                addRoute(locale, `/${route["en"]}`, `/${route[locale]}`);
            }
        }

        editorialRoutes.forEach(({ path }) => {
            const defaultBase = path["/"]["en"];

            const baseKey = `/${defaultBase}/[[...slug]]`;

            const localizedBase = path["/"]?.[locale];
            const localizedRoute = localizedBase
                ? `/${localizedBase}/[[...slug]]`
                : `/${defaultBase}/[[...slug]]`;

            addRoute(locale, baseKey, localizedRoute);
        });

        const orderConfirmationLocalizedRoute = `/${orderPagePath["/"][locale]}/${orderConfirmationPagePath["/"][locale]}/[id]`;
        const orderConfirmationRoute = `/${orderPagePath["/"]["en"]}/${orderConfirmationPagePath["/"]["en"]}/[id]`;
        addRoute(locale, orderConfirmationRoute, orderConfirmationLocalizedRoute);

        const legalLocalizedRoute = `/${customerServicePagePath["/"][locale]}/${customerServiceLegalPages["/"][locale]}/[...slug]`;
        const legalRoute = `/${customerServicePagePath["/"]["en"]}/${customerServiceLegalPages["/"]["en"]}/[...slug]`;
        addRoute(locale, legalRoute, legalLocalizedRoute);

        const productRoute = `/${productPagePath["/"]["en"]}/[productName]/[articleNumber]/[colorCode]`;
        const productLocalizedRoute = `/${productPagePath["/"][locale]}/[productName]/[articleNumber]/[colorCode]`;
        addRoute(locale, productRoute, productLocalizedRoute);

        for (const page of myAccountPagesList) {
            const localizedRoute = `/${myAccountPagePath["/"][locale]}/${page.path["/"][locale]}`;
            const route = `/${myAccountPagePath["/"]["en"]}/${page.path["/"]["en"]}`;
            addRoute(locale, route, localizedRoute);
        }

        categoryRoutes.forEach(({ path }) => {
            const defaultBase = path["/"]["en"];
            const baseKey = `/${defaultBase}/[...categories]`;
            const localizedBase = path["/"]?.[locale];
            const localizedRoute = localizedBase
                ? `/${localizedBase}/[...categories]`
                : `/${defaultBase}/[...categories]`;

            addRoute(locale, baseKey, localizedRoute);
        });
    }

    return pathnames;
};

export const routing = defineRouting({
    locales: locales,
    defaultLocale: defaultLocale,
    alternateLinks: false,
    localePrefix: locales.length > 1 ? "as-needed" : "never",
    localeDetection: false,
    pathnames: generatePathnames(locales),
});

export const pathnames = routing.pathnames;

export const { Link, redirect, usePathname, useRouter, getPathname, permanentRedirect } =
    createNavigation(routing);
